import type { KatalogEintrag, VertragPosition } from "../lib/wizard";
import { einsaetze, istNebenkosten } from "../lib/frequenz";
import { positionTitel } from "../lib/wizard";
import "../styles/nebenkosten.css";

/**
 * Wonach sich eine Nebenkostenposition richtet — Anfahrt, Entsorgung, Maschine.
 *
 * Bis zum 28.09.2026 hatten diese Positionen feste Monate. Nahm der Kunde auf
 * der Angebotsseite eine Leistung weg, bezahlte er ihre Anfahrt weiter
 * (Benjamins Feedback, Punkt 1). Jetzt folgen sie Leistungen (Mig 187):
 *
 * - **allen Einsätzen** — die übliche Anfahrt, eine je Besuch
 * - **bestimmten Leistungen** — die zweite Anfahrt für den zweiten Tag der
 *   Großen Gartenpflege; die Entsorgung nur beim Gehölzschnitt, nicht beim Mähen
 * - **von Hand** — die Monate im Raster darunter gelten, wie sie gesetzt sind
 *
 * Ausgewählt wird über Katalogleistungen, nicht über Positionen: beim
 * Speichern entstehen die Positionen neu, ihre IDs überleben das nicht.
 */
export default function NebenkostenBezug({ p, positionen, katalog, onPatch }: {
  p: VertragPosition;
  positionen: VertragPosition[];
  katalog: KatalogEintrag[];
  onPatch: (patch: Partial<VertragPosition>) => void;
}) {
  const folgt = p.folgt ?? null;
  const art = folgt === null ? "alle" : folgt.length === 0 ? "hand" : "nur";

  // Wählbar ist jede Katalogleistung dieses Vertrages, die selbst ein Termin ist.
  const leistungen = positionen
    .filter(x => x.leistung_id && !istNebenkosten(x, katalog))
    .filter((x, i, alle) => alle.findIndex(y => y.leistung_id === x.leistung_id) === i);

  function nurUmschalten(id: string) {
    const jetzt = new Set(folgt || []);
    if (jetzt.has(id)) jetzt.delete(id); else jetzt.add(id);
    // Die letzte abgewählte Leistung heißt nicht „von Hand" — das wäre ein
    // Moduswechsel durch die Hintertür. Dann eben wieder alle Einsätze.
    onPatch({ folgt: jetzt.size ? [...jetzt] : null });
  }

  return (
    <div className="pf-nk">
      <div className="pf-nk-kopf">Richtet sich nach</div>
      <div className="pf-nk-wahl" role="radiogroup" aria-label="Wonach sich diese Position richtet">
        <button type="button" role="radio" aria-checked={art === "alle"}
          className={art === "alle" ? "is-on" : ""} onClick={() => onPatch({ folgt: null })}>
          allen Einsätzen
        </button>
        <button type="button" role="radio" aria-checked={art === "nur"}
          className={art === "nur" ? "is-on" : ""} disabled={leistungen.length === 0}
          onClick={() => onPatch({
            folgt: leistungen.length ? [leistungen[0].leistung_id as string] : null,
          })}>
          bestimmten Leistungen
        </button>
        <button type="button" role="radio" aria-checked={art === "hand"}
          className={art === "hand" ? "is-on" : ""} onClick={() => onPatch({ folgt: [] })}>
          von Hand
        </button>
      </div>

      {art === "nur" && (
        <div className="pf-nk-leistungen">
          {leistungen.map(x => {
            const an = (folgt || []).includes(x.leistung_id as string);
            return (
              <label key={x.leistung_id} className={"pf-nk-chip" + (an ? " is-on" : "")}>
                <input type="checkbox" checked={an}
                  onChange={() => nurUmschalten(x.leistung_id as string)} />
                {positionTitel(x, katalog)}
              </label>
            );
          })}
        </div>
      )}

      <div className="pf-nk-info">
        {art === "hand"
          ? "Die Monate unten gelten so, wie sie gesetzt sind."
          : `${einsaetze(p)}× im Jahr — rechnet sich mit, wenn Leistungen sich ändern, auch beim Kunden.`}
      </div>
    </div>
  );
}
